import {
  Col,
  Row,
  TableColumnsType,
  Tooltip,
  Button as ButtonAnt,
  Modal,
  App,
  Typography
} from 'antd';
import Paragraph from 'antd/es/typography/Paragraph';
import { DeleteOutlined, EditOutlined, EyeOutlined } from '@ant-design/icons';
import IMAGE_DEFAULT from '@/assets/images/image_default.png';
import { PAGINATION_PARAMS } from '@/constants/pagination.constant';
import { Table } from '@/components/tables/table';
import { css } from '@emotion/react';
import { useCallback, useEffect, useState } from 'react';
import { FoodManagementNutrientDetails } from '@/components/forms/food-management/nutrients.form';
import { useModal } from '@/hooks/modal.hook';
import { DishManagementSearcher } from '@/components/forms/dish-management/searcher.form';
import DISH_STATISTICS from '@/constants/dish-statistics.constant';
import { formatDateToHHmmDDMMYYYY, tableScrollY } from '@/utils/common';
import { PermissionMode } from '@/enums/mode.enum';
import DishInformationModal from '@/components/modals/dish-management/information.modal';
import {
  getListDish,
  deleteDish,
  watchDishPagination,
  watchListDish,
  watchListDishSalt,
  watchListDishAge,
  watchListDishCookingMethod,
  watchListSourceDish,
  watchListStructureMealDish,
  watchListSizeDish,
  watchListRegionDish,
  watchListTagDish,
  watchDishLoading,
  watchDishMessage,
  resetMessages
} from '@/redux/slices/dish.slice';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import dayjs from 'dayjs';
import { PageURLs } from '@/utils/navigate';
import { useQuery } from '@/hooks/query.hook';
import { setMessages } from '@/redux/slices/app.slice';

type PageChangeParams = {
  page?: number;
  pageSize?: number;
  filter?: FilterDish;
};

interface DishParams {
  page: number;
  limit: number;
  name?: string;
  sourceDish?: string;
  structureMealDish?: string;
  dishCookingMethod?: string;
  dishAge?: string;
  dishLabel?: string;
  region?: string;
  saltRecipe?: string;
  startDate?: string;
  endDate?: string;
  from_date?: string;
  to_date?: string;
}

type DishSliderProps = {
  onSearchExport: (filters: Record<string, any>) => void;
  setCurrentPage?: React.Dispatch<React.SetStateAction<number>>;
  setPageSize?: React.Dispatch<React.SetStateAction<number>>;
};

const DishManagementTable = ({
  onSearchExport,
  setCurrentPage,
  setPageSize
}: DishSliderProps) => {
  const { modal } = App.useApp();
  const dispatch = useAppDispatch();
  const [filter, setFilter] = useState<FilterDish>({});
  const [selected, setSelected] = useState(undefined);
  const listDish = useAppSelector(watchListDish);
  const pagination = useAppSelector(watchDishPagination);
  const loading = useAppSelector(watchDishLoading);
  const listRegion = useAppSelector(watchListRegionDish);
  const listDishSalt = useAppSelector(watchListDishSalt);
  const listDishAge = useAppSelector(watchListDishAge);
  const listDishCookingMethod = useAppSelector(watchListDishCookingMethod);
  const listSourceDish = useAppSelector(watchListSourceDish);
  const listStructureMealDish = useAppSelector(watchListStructureMealDish);
  const listSizeDish = useAppSelector(watchListSizeDish);
  const listTagDish = useAppSelector(watchListTagDish);
  const messageInfo = useAppSelector(watchDishMessage);
  const { openModal, closeModal } = useModal();
  const { push } = useQuery();
  const [currentPage, setCurrentPageState] = useState(1);
  const [pageSize, setPageSizeState] = useState(10);

  const handlePageChange = useCallback(
    ({
      page = PAGINATION_PARAMS.offset,
      pageSize: newPageSize = PAGINATION_PARAMS.limit,
      filter
    }: PageChangeParams) => {
      const params: DishParams = {
        page: page,
        limit: newPageSize
      };
      if (filter?.name) {
        params.name = filter.name;
      }
      if (filter?.sourceDish) {
        params.sourceDish = filter.sourceDish;
      }
      if (filter?.structureMealDish) {
        params.structureMealDish = filter.structureMealDish;
      }
      if (filter?.dishCookingMethod) {
        params.dishCookingMethod = filter.dishCookingMethod;
      }
      if (filter?.dishAge) {
        params.dishAge = filter.dishAge;
      }
      if (filter?.dishLabel) {
        params.dishLabel = filter.dishLabel;
      }
      if (filter?.region) {
        params.region = filter.region;
      }
      if (filter?.saltRecipe) {
        params.saltRecipe = filter.saltRecipe;
      }
      if (filter?.startDate) {
        params.startDate = filter.startDate;
      }
      if (filter?.endDate) {
        params.endDate = filter.endDate;
      }
      let update_from_date = '';
      let update_to_date = '';
      const { update_from_date: from, update_to_date: to } = filter as any;
      if (from) {
        update_from_date = dayjs(from).format('YYYY-MM-DD');
      }
      if (to) {
        update_to_date = dayjs(to).format('YYYY-MM-DD');
      }
      const payload = {
        name: params.name || '',
        external_dish_type_ids: params.sourceDish || [],
        meal_structure_ids: params.structureMealDish || [],
        cooking_method_ids: params.dishCookingMethod || [],
        age_group_ids: params.dishAge || [],
        tag_names: params.dishLabel || [],
        region_ids: params.region || [],
        formula_types: params.saltRecipe || [],
        update_from_date,
        update_to_date
      }
      dispatch(getListDish({
        query: payload,
        offset: page,
        limit: newPageSize
      }));
    },
    [dispatch]
  );

  const handleSearch = (values: FilterDish) => {
    const isReset = Object.keys(values).length === 0;
    const newFilter = isReset ? {} : { ...filter, ...values };
    setFilter(newFilter);
    setCurrentPageState(1);
    setPageSizeState(10);
    onSearchExport(newFilter);
    handlePageChange({ page: 1, pageSize: 10, filter: newFilter });
  };

  const handleViewInformation = (dish: { id: string; ageGroupId: string, dishSizeId: string }) => {
    openModal(<DishInformationModal
      dish={dish}
      onCancel={closeModal}
      listDishAge={listDishAge}
      listSourceDish={listSourceDish}
      listSizeDish={listSizeDish}
      listRegion={listRegion}
      listTagDish={listTagDish} />, {
      width: 1200
    });
  };

  const handleDeleteDish = async (dishId: number, formulaId: string) => {
    await dispatch(deleteDish({dishId, formulaId}));
    const remainingRecords = listDish.length - 1;
    if (remainingRecords === 0 && pagination.curPage > 1) {
      handlePageChange({
        page: pagination.curPage - 1,
        pageSize: pagination.limitPage,
        filter
      });
    } else {
      handlePageChange({
        page: pagination.curPage,
        pageSize: pagination.limitPage,
        filter
      });
    }
  };
  useEffect(() => {
    dispatch(getListDish({}));
  }, [dispatch]);

  const handleEditInformation = (dish: { id: string; ageGroupId: string; dishSizeId: string }) => {
    push(
      `${PageURLs.ofDishManagement()}?mode=${PermissionMode.EDIT}&dishId=${dish.id}&ageGroupId=${dish.ageGroupId}&dishSizeId=${dish.dishSizeId}`
    );
  };

  const confirmDeleteDish = (dishId: number, formulaId: string) => {
    modal.confirm({
      title: 'Bạn có chắc chắn xoá món ăn này?',
      content: (
        <em>
          Dữ liệu không thể khôi phục
        </em>
      ),
      okText: 'Xóa',
      width: 450,
      cancelText: 'Hủy',
      onOk: () => handleDeleteDish(dishId, formulaId)
    });
  };

  const statisticColumns: TableColumnsType<any> = Object.keys(
    DISH_STATISTICS
  ).map((key, _) => ({
    title: DISH_STATISTICS[key].title,
    dataIndex: key,
    key,
    width: 50,
    render: (_, record) => {
      const totalField = 'total' + DISH_STATISTICS[key].name.charAt(0).toUpperCase() + DISH_STATISTICS[key].name.slice(1);
      return record[totalField] && record[totalField] + DISH_STATISTICS[key]?.unit;
    }
  }));

  useEffect(() => {
    if (messageInfo) {
      dispatch(
        setMessages([
          {
            type: messageInfo.type,
            message: messageInfo.message,
            description: messageInfo.description
          }
        ])
      );
      dispatch(resetMessages());
    }
  }, [dispatch, messageInfo]);

  const columns: TableColumnsType<any> = [
    {
      title: 'STT',
      width: 60,
      render: (_, __, index) => { return(pagination.curPage - 1) * pagination.limitPage + index + 1 },
    },
    {
      title: 'ID món ăn',
      dataIndex: 'dishId',
      key: 'dishId',
      width: 100,
    },
    {
      title: 'Tên món ăn',
      dataIndex: 'name',
      key: 'name',
      width: 200,
      fixed: 'left'
    },

    {
      title: 'Thumbnail',
      dataIndex: 'sizeImage',
      key: 'sizeImage',
      width: 100,
      render: (record) => (
        <img
          //   src={record ? `${imageUrl}${record}` : IMAGE_DEFAULT}
          src={record ? `${record}` : IMAGE_DEFAULT}
          alt='Food Thumbnail'
          style={{ width: '50px', height: '32px', objectFit: 'cover' }}
        />
      )
    },
    {
      title: 'Độ tuổi',
      width: 215,
      dataIndex: 'ageGroupId',
      key: 'ageGroupId',
      render: (ageId: number | string) => {
        const age = listDishAge.find((r) => r.id === ageId);
        return age?.name || '-';
      }
    },
    {
      title: 'Nhãn món ăn',
      dataIndex: 'tags',
      key: 'tags',
      width: 120,
      render: (tagIds: number | string) => {
        if (!tagIds) return '-';

        const tagIdArray = typeof tagIds === 'string'
          ? tagIds.split(';').map(tag => tag.trim())
          : [String(tagIds)];
        const matchedTags = listTagDish
          .filter((tag) => tagIdArray.includes(tag.tagName))
          .map((tag) => tag.tagName);

        if (matchedTags.length === 0) return '-';
        const tagText = matchedTags.join('; ');

        return (
          <Paragraph ellipsis={{ rows: 1 }} style={{ margin: 0, maxWidth: 120 }}>
            {tagText}
          </Paragraph>
        );
      }
    },
    {
      title: 'Miền',
      dataIndex: 'regionId',
      key: 'regionId',
      width: 105,
      render: (regionId: number | string) => {
        const region = listRegion.find((r) => r.regionId === regionId);
        return region?.regionName || '-';
      }
    },
    {
      title: 'Nguồn món ăn',
      dataIndex: 'externalDishTypeId',
      key: 'externalDishTypeId',
      width: 200,
      render: (externalDishId: number | string) => {
        const externalDish = listSourceDish.find((r) => r.id === externalDishId);
        return externalDish?.name || '-';
      }
    },
    {
      title: 'Cỡ món ăn',
      dataIndex: 'sizeType',
      key: 'sizeType',
      width: 120,
      render: (sizeDishId: number | string) => {
        const sizeDish = listSizeDish.find((r) => r.id === sizeDishId);
        return sizeDish?.name || '-';
      }
    },
    {
      title: 'Công thức muối',
      dataIndex: 'formulaType',
      key: 'formulaType',
      width: 135,
      // render: (saltDishId: number | string) => {
      //   const saltDish = listDishSalt.find((r) => r.formulaType === saltDishId);
      //   return saltDish?.formulaType || '-';
      // }
    },
    ...(statisticColumns as TableColumnsType<FoodNutrient>),
    {
      title: 'Nhóm món ăn theo phương pháp nấu',
      dataIndex: 'cookingMethodId',
      key: 'cookingMethodId',
      width: 275,
      render: (cookingMethodId: string) => {
        const method = listDishCookingMethod.find((r) => r.id === cookingMethodId);
        return method?.name || '-';
      }
    },
    {
      title: 'Nhóm món ăn theo cấu trúc bữa ăn',
      dataIndex: 'mealStructureId',
      key: 'mealStructureId',
      width: 215,
      render: (structureId: number | string) => {
        const structure = listStructureMealDish.find((r) => r.id === structureId);
        return structure?.name || '-';
      }
    },
    {
      title: 'Cập nhật mới nhất',
      dataIndex: 'updatedAt',
      key: 'updatedAt',
      width: 200,
      render: (_, record) => {
        const updatedAt = record.updatedAt;
        const updatedBy = record.updatedByName;
        return `${formatDateToHHmmDDMMYYYY(updatedAt)}${updatedBy ? ' [' + updatedBy + ']' : ''}`;
      }
    },
    {
      title: 'Ngày tạo',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 200,
      render: (_, record) => {
        const createdAt = record.createdAt;
        const createdBy = record.createdByName;
        return `${formatDateToHHmmDDMMYYYY(createdAt)}${createdBy ? ' [' + createdBy + ']' : ''}`;
      }
    },
    {
      title: 'Thao tác',
      key: 'action',
      fixed: 'right',
      width: 104,
      render: (_, record) => (
        <Row gutter={4} wrap={false}>
          <Col>
            <Tooltip title='Xem thông tin'>
              <ButtonAnt
                type='primary'
                onClick={() => handleViewInformation({ id: String(record.dishId), ageGroupId: String(record.ageGroupId), dishSizeId: record.dishSizeId })}
                icon={<EyeOutlined />}
                css={viewButtonStyles}
              />
            </Tooltip>
          </Col>
          <Col>
            <Tooltip title='Cập nhật'>
              <ButtonAnt
                type='primary'
                onClick={() => handleEditInformation({ id: String(record.dishId), ageGroupId: String(record.ageGroupId), dishSizeId: record.dishSizeId })}
                icon={<EditOutlined />}
                css={editButtonStyles}
              />
            </Tooltip>
          </Col>
          <Col>
            <Tooltip title='Xoá'>
              <ButtonAnt
                onClick={() => confirmDeleteDish(record.dishId, record.id)}
                type='primary'
                danger
                icon={<DeleteOutlined />}
              />
            </Tooltip>
          </Col>
        </Row>
      )
    }
  ];
  return (
    <div css={rootStyles}>
      <DishManagementSearcher onSearch={handleSearch}
        listDishSalt={listDishSalt}
        listDishAge={listDishAge}
        listDishCookingMethod={listDishCookingMethod}
        listSourceDish={listSourceDish}
        listStructureMealDish={listStructureMealDish}
        listRegionDish={listRegion}
        listTagDish={listTagDish}
      />
      <div css={boxStyles}>
        <Typography.Title level={5}>Danh sách món ăn</Typography.Title>

        <Table<any>
          columns={columns}
          dataSource={listDish}
          loading={loading}
          scrollY={tableScrollY(pageSize)}
          pagination={{
            showQuickJumper: true,
            showSizeChanger: true,
            current: currentPage,
            pageSize: pageSize,
            total: pagination.totalRows,
            onChange: (page, newPageSize) => {
              setCurrentPageState(page);
              setPageSizeState(newPageSize);
              if (setCurrentPage) setCurrentPage(page);
              if (setPageSize) setPageSize(newPageSize);
              handlePageChange({ page, pageSize: newPageSize, filter });
            }
          }}
        />

        <Modal
          open={selected}
          onCancel={(e) => {
            e.stopPropagation();
            setSelected(undefined);
          }}
          width={1000}
        >
          <FoodManagementNutrientDetails food={selected} />
        </Modal>
      </div>
    </div>
  );
}
export default DishManagementTable;
const rootStyles = css`
  background: var(--gray-soft-color);
  display: flex;
  flex-direction: column;
  gap: 1.4rem;
  min-height: calc(100vh - var(--header-height));
`;

const boxStyles = css`
  padding: 1rem;
  background: var(--white-color);
  box-shadow: 0.02rem 0.04rem 0.2rem var(--gray-light-color);
  &:nth-of-type(2) {
    padding: 1.6rem 1rem;
  }
      
  .ant-table-thead > tr > th {
    background: var(--blue-dark-color) !important;
    color: var(--white-color);
  }

  .ant-table-cell::before {
    display: none;
  }
`;

const viewButtonStyles = css`
  background-color: var(--green-dark-color) !important;
  color: var(--white-color);
`;

const editButtonStyles = css`
  background-color: var(--yellow-light-color) !important;
  color: var(--white-color);
`;
